import styled from "@emotion/styled";
import { Instant, LocalDate } from "@js-joda/core";
import { useMutation } from "@tanstack/react-query";
import assertNever from "assert-never";
import ons from "onsenui";
import * as React from "react";
import * as Ons from "react-onsenui";
import RequiredDocDropdownActivity from "../activities/RequiredDocDropdownActivity";
import { deepEqual } from "../deep-equal";
import {
  Icd10Codes,
  MedicationProfileSearchResponse,
  NewPhysicianCreated,
  NewPhysicianParams,
  PatientAvailablePhysicians,
  PatientContractDiagnosisCodeId,
  PatientContractDiagnosisCodes,
  PatientContractDiagnosisSetParams,
  PatientId,
  PatientMedicationProfile,
  PatientMedicationProfileCreateParams,
  PatientMedicationProfileWithDetails,
  PatientPrimaryPhysician,
  PhysicianId,
} from "../messages";
import { PatientMedicationProfileItem } from "../messages/MedicationProfile";
import {
  CheckForOtherNursingQuestionsOnDocumentsResponse,
  DocumentItemQuestionType,
  DocumentReplaceConstants,
  PatientDocumentAnswerObject,
  PatientDocumentAnswers,
  PatientDocumentContentItem,
  PatientDocumentPOCItem,
  PatientDocumentScanCreate,
  POCItemsDocumentAnswer,
} from "../messages/PatientDocuments";
import { VitalExtraDetails } from "../messages/Vital";
import { STRINGS } from "../strings";
import { globalize, translate } from "../translate";
import { CAMERA_SVG, COPY_SVG } from "../utils/svgs";
import { undefinedToNull } from "../utils/undefinedToNull";
import { useModalV2 } from "./AppModals";
import { CardButton } from "./CardButton/CardButton";
import { parseErrorResponse } from "./caregiverPatientDocumentUtils";
import CustomSignature from "./CustomSignature";
import { BodyDrawing } from "./DocScreen/components/BodyDrawing";
import DocumentUploadActionsActivity from "./DocumentUploadActionsActivity";
import { AppButton } from "./Generic/AppButton";
import { AppCard, AppCardContent, AppCardHeader, AppCardTitle } from "./Generic/AppCard";
import AppModal from "./Generic/AppModal/AppModal";
import { AppOverlay } from "./Generic/AppOverlay";
import { isDefined } from "./Generic/IsDefined";
import { openDatepicker } from "./MedflytDatepicker";
import MedicationProfileDocumentSection from "./MedicationProfile/components/MedicationProfileDocumentSection";
import PatientContractDiagnosisCodeActivities from "./PatientContractDiagnosisCode/PatientContractDiagnosisCodeActivities";
import QuestionErrorBottomSheet from "./QuestionErrorBottomSheet";
import QuestionHeader from "./QuestionHeader";
import { RequiredDocAnswerableQuestionProps } from "./RequiredDocAnswerableQuestion";
import RequiredDocBloodPressure from "./RequiredDocBloodPressure";
import { RequiredDocCheck } from "./RequiredDocCheck";
import RequiredDocDate from "./RequiredDocDate";
import RequiredDocFreeText from "./RequiredDocFreeText";
import RequiredDocImage from "./RequiredDocImage";
import RequiredDocLongText from "./RequiredDocLongText";
import RequiredDocNumber from "./RequiredDocNumber";
import { RequiredDocRadio } from "./RequiredDocRadio";
import RequiredDocShortText from "./RequiredDocShortText";
import { RequiredDocSignature } from "./RequiredDocSignature";
import RequiredDocTime from "./RequiredDocTime";
import RequiredDocYesNo from "./RequiredDocYesNo";
import ScaffoldTitle from "./Scaffold/components/ScaffoldTitle";
import Scaffold from "./Scaffold/Scaffold";
import { simpleAlert } from "./SimpleAlert";
import RequiredDocVitalSignActivity from "./VitalSign/components/RequiredDocVitalSignActivity";
import PatientPhysicianDetails from "./PatientPhysician/PatientPhysicianDetails";
import { POCDocumentQuestion } from "./POCDocumentQuestion/POCDocumentQuestion";
import { ResubmissionBanner } from "./ResubmissionBanner";

let saveTimeout: NodeJS.Timer | null = null;
let fadeoutTimeout: NodeJS.Timer | null = null;

export enum DocumentSubmissionDate {
  TODAY,
  SCHEDULED_DATE,
  DATE_PICKER_DATE,
}

export interface ScheduledVisitDocScreenProps {
  onBackClick: () => void;
  onAnswerQuestion: (
    id: number,
    row: number,
    value: any,
    onSuccess?: () => void,
    createdAt?: Instant,
    vitalExtraDetails?: VitalExtraDetails
  ) => Promise<void>;
  onSubmitForm: (
    submissionDate: LocalDate | null,
    regenerateNursingQuestionsOtherDocuments: boolean
  ) => Promise<void>;
  onValidateSubmissionDate: () => boolean;
  getIcdCodeAutoCompleteOptions: (searchText: string) => Promise<Icd10Codes>;
  title: string;
  docFormat: PatientDocumentContentItem[];
  initialAnswers?: PatientDocumentAnswerObject[];
  readonly: boolean;
  getLastAnswers?: () => Promise<PatientDocumentAnswers>;
  skipChartTree?: boolean;
  documentReplaceConstants: DocumentReplaceConstants;
  isScannable: boolean;
  onScanUpload: (base64: string, showLoader: () => void, hideLoader: () => void) => void;
  getScheduledDate: () => LocalDate | null;
  getPatientMedicationProfile: () => Promise<PatientMedicationProfile>;
  searchMedications: (value: string) => Promise<MedicationProfileSearchResponse>;
  onCreateMedication: (
    params: PatientMedicationProfileCreateParams
  ) => Promise<PatientMedicationProfileItem>;
  onUpdatedMedication: (medication: PatientMedicationProfileWithDetails) => Promise<void>;
  onDeleteMedication: (medication: PatientMedicationProfileWithDetails) => Promise<void>;
  getPatientContractDiagnosisCodes: () => Promise<PatientContractDiagnosisCodes>;
  onCreateDiagnosis: (
    params: PatientContractDiagnosisSetParams
  ) => Promise<PatientContractDiagnosisCodes>;
  onEditDiagnosis: (
    id: PatientContractDiagnosisCodeId,
    params: PatientContractDiagnosisSetParams
  ) => Promise<PatientContractDiagnosisCodes>;
  onDeleteDiagnosis: (id: PatientContractDiagnosisCodeId) => Promise<void>;
  patientId: PatientId;
  getPatientPrimaryPhysician: (patientId: PatientId) => Promise<PatientPrimaryPhysician>;
  addNewPhysician: (
    patientId: PatientId,
    params: NewPhysicianParams
  ) => Promise<NewPhysicianCreated>;
  setPatientPhysician: (patientId: PatientId, physicianId: PhysicianId) => Promise<void>;
  getAllPhysicians: (patientId: PatientId) => Promise<PatientAvailablePhysicians>;
  onClickAddPhysician: (patientId: PatientId) => void;
  resubmissionReason: string | null;
  onCheckForOtherNursingQuestionsOnDocuments: () => Promise<CheckForOtherNursingQuestionsOnDocumentsResponse>;
}

export interface ScheduledVisitDocScreenState {
  answers: any;
  showErrors: boolean;
  showRequiredFieldsDialog: boolean;
  submitting: boolean;
  showSubmitError: boolean;
  canSubmit: boolean;
  isSubmissionMissmatchOpen: boolean;
  isRegenerateOtherDocumentsModalOpen: boolean;
  disabledMessage: string | null;
  isScanningNewDocument: boolean;
  submitDisabled: boolean;
  submitDate: LocalDate | null;
  regenerateOtherDocuments: boolean;
  documentsToBeRegenerated: string[];
}

interface AnswerAndFormat {
  answerObject: any;
  format: PatientDocumentContentItem;
}

interface ParentChartSumObj {
  parentId: number;
  parentObj?: ParentChartSumObj;
  items: Set<number>;
  sumItems: number[];
  groupSums: Map<string, number>;
  children: ParentChartSumObj[];
  alwaysActiveCharts: Set<number>;
}

export class ScheduledVisitDocScreen<
  T extends ScheduledVisitDocScreenProps
> extends React.Component<T, ScheduledVisitDocScreenState> {
  chartParentsSumTree?: ParentChartSumObj;
  idToContentItem: Map<number, PatientDocumentContentItem>;
  sentReqCount: number;
  finishedTypingTimeout: any;

  constructor(props: T) {
    super(props);

    this.state = {
      answers: {
        ...getEmptyFormat(props.docFormat),
      },
      showErrors: false,
      showRequiredFieldsDialog: false,
      submitting: false,
      showSubmitError: false,
      canSubmit: true,
      isSubmissionMissmatchOpen: false,
      isRegenerateOtherDocumentsModalOpen: false,
      disabledMessage: null,
      isScanningNewDocument: false,
      submitDisabled: false,
      submitDate: null,
      regenerateOtherDocuments: false,
      documentsToBeRegenerated: [],
    };

    this.idToContentItem = this.getIdToContentItems(
      this.props.docFormat,
      new Map<number, PatientDocumentContentItem>()
    );
    if (this.props.skipChartTree !== true) {
      this.chartParentsSumTree = this.getChartSumsObjTree(this.props.docFormat);
    }
    if (props.initialAnswers !== undefined) {
      this.initAnswers(props.initialAnswers, this.state.answers, true);
    }

    this.sentReqCount = 0;
  }

  findItemByIdFromArray(
    id: number,
    docFormat: PatientDocumentContentItem[]
  ): PatientDocumentContentItem | null {
    let found: null | PatientDocumentContentItem = null;

    docFormat.forEach((item: PatientDocumentContentItem) => {
      if (found !== null) {
        return;
      }

      if (item.id === id) {
        found = item;
      } else if (item.columns !== null && item.columns !== undefined) {
        found = this.findItemByIdFromArray(id, item.columns[0]);
      }
    });

    return found;
  }

  getIdToContentItems(
    content: PatientDocumentContentItem[],
    map: Map<number, PatientDocumentContentItem>
  ): Map<number, PatientDocumentContentItem> {
    content.forEach((contentItem) => {
      map.set(contentItem.id, contentItem);

      if (
        contentItem.columns !== undefined &&
        contentItem.columns !== null &&
        contentItem.columns.length > 0
      ) {
        this.getIdToContentItems(contentItem.columns[0], map);
      }
    });

    return map;
  }

  shouldComponentUpdate(
    nextProps: ScheduledVisitDocScreenProps,
    nextState: ScheduledVisitDocScreenState
  ) {
    return !deepEqual(this.props, nextProps) || !deepEqual(this.state, nextState);
  }

  initAnswers(answers: PatientDocumentAnswerObject[], stateAnswers: any, keepSigs: boolean) {
    answers.forEach((answer) => {
      const curItem = this.findItemByIdFromArray(answer.questionId, this.props.docFormat);
      if (
        curItem !== null &&
        (this.props.readonly === true ||
          (!keepSigs
            ? curItem.itemType !== "patientSignature" && curItem.itemType !== "employeeSignature"
            : true))
      ) {
        const a = this.createAnswerPath(answer.questionId, answer, stateAnswers).answerObject;
        a[answer.questionId] = answer.answer;
      }
    });
  }

  createAnswerPath(
    questionId: number,
    answer: PatientDocumentAnswerObject,
    answers: any
  ): AnswerAndFormat {
    const curItem = this.findItemByIdFromArray(questionId, this.props.docFormat);
    let chartRow: number | undefined;

    if (curItem === null) {
      throw new Error("Error in document format, question id wasn't found");
    }

    let returnState = answers;

    if (curItem.parentId !== null) {
      const parent: AnswerAndFormat = this.createAnswerPath(curItem.parentId, answer, answers);

      returnState = parent.answerObject;

      if (parent.format.itemType === "chart") {
        chartRow = answer.chartRow as number;
        const columns = (parent.format.columns as [PatientDocumentContentItem[]])[0];

        // Creates empty rows up until this answer
        for (let i = 0; i < chartRow; i++) {
          if (returnState[i] === undefined) {
            returnState[i] = getEmptyFormat(columns);
          }
        }

        if (returnState[chartRow] === undefined || returnState[chartRow] === null) {
          returnState[chartRow] = getEmptyFormat(columns);
        }
        return { answerObject: returnState[chartRow], format: curItem };
      }
    }
    if (curItem.itemType === "chart") {
      return { answerObject: returnState[curItem.id], format: curItem };
    } else {
      return { answerObject: returnState, format: curItem };
    }
  }

  getAnswer = (id: number, answers?: any): any => {
    const answer = answers === undefined ? this.state.answers[id] : answers[id];
    // if (typeof answer === "string") {
    //     return answer.toLowerCase();
    // }
    return answer;
  };

  getAnswersValue = (id: number, allAnswers: any, answer?: string | string[]): number => {
    const format = this.idToContentItem.get(id) as PatientDocumentContentItem;
    let answers = answer !== undefined ? answer : allAnswers[id];

    if (!Array.isArray(answers)) {
      answers = [answers];
    }

    return answers.reduce((sum: number, ans: string) => {
      if (
        format.answerValues !== undefined &&
        format.possibleAnswers !== undefined &&
        format.possibleAnswers.indexOf(ans) !== -1
      ) {
        const ansValue = format.answerValues[format.possibleAnswers.indexOf(ans)];
        return sum + ansValue;
      } else {
        return sum;
      }
    }, 0);
  };

  // Not meant for charts.
  getAnswerAndChildrenValue = (
    id: number,
    sumMap: Map<string, number>,
    answers: any,
    activeQuestionIds: Set<number>
  ): Map<string, number> => {
    const format = this.idToContentItem.get(id) as PatientDocumentContentItem;
    if (
      format.isInValueGroup !== undefined &&
      format.isInValueGroup &&
      format.valueGroup !== undefined &&
      format.valueGroup !== null
    ) {
      const ansValue = this.getAnswersValue(id, answers, answers[id]);
      const groupValue = sumMap.get(format.valueGroup);
      sumMap.set(format.valueGroup, (groupValue !== undefined ? groupValue : 0) + ansValue);
    }

    if (
      format.itemType !== "chart" &&
      format.columns !== undefined &&
      format.columns !== null &&
      format.columns.length > 0
    ) {
      format.columns[0].forEach((questionItem) => {
        if (
          questionItem.showIfParentEquals === null ||
          (questionItem.showIfParentEquals && questionItem.ifParentEquals === answers[id])
        ) {
          this.getAnswerAndChildrenValue(questionItem.id, sumMap, answers, activeQuestionIds);
        }
      });
    }
    activeQuestionIds.add(id);

    return sumMap;
  };

  combineAnswerMap = (
    source: Map<string, number>,
    copyFrom: Map<string, number>
  ): Map<string, number> => {
    copyFrom.forEach((groupSum, group) => {
      const targetSum = source.get(group);
      const lastSum = targetSum !== undefined ? targetSum : 0;
      source.set(group, lastSum + groupSum);
    });

    return source;
  };

  getChartSumsObjTree = (format: PatientDocumentContentItem[]): ParentChartSumObj => {
    let itemQueue = format;
    const sumMap = new Map<number, ParentChartSumObj>();
    const parentIds = new Map<number, number>();

    sumMap.set(-1, {
      parentId: -1,
      items: new Set<number>(),
      sumItems: [],
      groupSums: new Map<string, number>(),
      children: [] as ParentChartSumObj[],
      alwaysActiveCharts: new Set<number>(),
    });

    let i = 0;
    while (i < itemQueue.length) {
      const item = itemQueue[i];
      const parentId =
        parentIds.get(item.id) !== undefined ? (parentIds.get(item.id) as number) : -1;

      // Both charts and -1 define closed total sum tree for upwards search.
      if (item.itemType === "chart") {
        sumMap.set(item.id, {
          parentId: item.id,
          items: new Set<number>(),
          groupSums: new Map<string, number>(),
          children: [] as ParentChartSumObj[],
          sumItems: [],
          alwaysActiveCharts: new Set<number>(),
        });

        if (item.columns !== undefined && item.columns !== null && item.columns.length > 0) {
          itemQueue = itemQueue.concat(item.columns[0]);
          item.columns[0].forEach((col) => {
            parentIds.set(col.id, item.id);
          });
        }
      }

      const chartParent = this.getHoldingChartByParentId(
        sumMap.get(-1) as ParentChartSumObj,
        parentId
      );
      const sumObj = sumMap.get(chartParent) as ParentChartSumObj;

      if (item.itemType === "chart") {
        const chartObj = sumMap.get(item.id) as ParentChartSumObj;
        sumObj.children.push(chartObj);
        chartObj.parentObj = sumObj;

        if (parentId === chartParent) {
          sumObj.alwaysActiveCharts.add(item.id);
        }
      } else {
        if (this.isDocItemTotalSumItem(item)) {
          sumObj.sumItems.push(item.id);
        } else if (parentId === chartParent) {
          sumObj.items.add(item.id);
        }
        if (item.columns !== null && item.columns !== undefined && item.columns.length > 0) {
          itemQueue = itemQueue.concat(item.columns[0]);
          item.columns[0].forEach((col) => {
            parentIds.set(col.id, item.id);
          });
        }
      }

      i++;
    }

    return sumMap.get(-1) as ParentChartSumObj;
  };

  // Flattens the tree to chart subtrees.
  getHoldingChartByParentId = (chartSumObj: ParentChartSumObj, parentId: number): number => {
    let searchQueue = [chartSumObj];

    let i = 0;
    while (i < searchQueue.length) {
      const currItem = searchQueue[i];
      if (currItem.items.has(parentId) || currItem.parentId === parentId) {
        return currItem.parentId;
      }

      searchQueue = searchQueue.concat(currItem.children);
      i++;
    }

    return -1;
  };

  handleOnInsertAnswer = (id: number, value: any, vitalExtraDetails?: VitalExtraDetails) => {
    const req = this.props.onAnswerQuestion(
      id,
      -1,
      value,
      () => showDocumentSaved(),
      Instant.now(),
      vitalExtraDetails
    );
    this.silentHandleOnInsertAnswer(id, value);
    this.sentReqCount += 1;
    if (this.state.canSubmit) {
      this.setState({
        canSubmit: false,
      });
    }

    return req
      .then(() => {
        this.sentReqCount -= 1;
        if (this.sentReqCount === 0) {
          clearTimeout(this.finishedTypingTimeout);
          this.finishedTypingTimeout = setTimeout(() => {
            this.setState({
              canSubmit: true,
              submitDisabled: false,
            });
          }, 250);
        }
      })
      .catch(() => {
        const item = this.idToContentItem.get(id);
        if (item?.isRequired === true) {
          this.silentHandleOnInsertAnswer(id, null);
          this.setState({
            disabledMessage: translate(STRINGS.AN_ERROR_HAS_OCCURED) + "\nPlease try again.",
            showErrors: true,
            submitDisabled: true,
          });
        }

        this.sentReqCount -= 1;
        if (this.sentReqCount === 0) {
          clearTimeout(this.finishedTypingTimeout);

          this.finishedTypingTimeout = setTimeout(() => {
            this.setState({
              canSubmit: true,
            });
          }, 250);
        }
      });
  };

  silentHandleOnInsertAnswer = (id: number, value: any) => {
    const sumAnswerChanges: any = [];
    this.setState(
      (prevState) => {
        const answers = {
          ...prevState.answers,
        };
        if (answers[id] === value) {
          return prevState;
        }
        answers[id] = value;

        if (this.props.skipChartTree !== true) {
          this.validateSumTree(
            this.chartParentsSumTree as ParentChartSumObj,
            answers,
            new Map<string, number>(),
            sumAnswerChanges
          );
        }

        return {
          ...prevState,
          answers: answers,
        };
      },
      () => {
        if (this.props.skipChartTree !== true) {
          sumAnswerChanges.forEach((change: any) => {
            this.props.onAnswerQuestion(
              change.questionId,
              change.chartRow,
              change.answer,
              () => showDocumentSaved(),
              Instant.now()
            );
          });
        }
      }
    );
  };

  // copyExportedQuestion = (id: number, chartRow: number, exportedQuestionId: number, exportedQuestionDocumentType: PatientDocumentTypeId, createdAt?: Instant) => {
  //     this.props.copyExportedQuestion(id, chartRow, exportedQuestionId, exportedQuestionDocumentType, createdAt !== undefined ? createdAt : Instant.now()).then((answer: PatientDocumentAnswerObject) => {
  //         this.silentHandleOnInsertAnswer(answer.questionId, answer.answer);
  //     }).catch((_error) => {
  //         // Navigate to document.
  //         ons.notification.alert({
  //             animation: "none",
  //             modifier: "material warning",
  //             title: translate(STRINGS.NO_ANSWERS_TO_COPY_TITLE),
  //             message: translate(STRINGS.NO_ANSWERS_TO_COPY)
  //         });
  //     });
  // }

  isDocItemTotalSumItem = (docItem: PatientDocumentContentItem): boolean => {
    return (
      docItem.isInValueGroup === true &&
      docItem.valueGroup !== undefined &&
      docItem.valueGroup !== null &&
      docItem.itemType === "number"
    );
  };

  // Unused variable for passing down.
  // This works by spreading through the tree from a specific node (starts at -1) and spreads everywhere until charts.
  // It sums up all the values it can get and passes it down.
  // On the way up it fills up all its totalSum questions (because it has all the sums from the way down) and passes it up
  // So it can be used on the way up to fill up the upper tree's sums and up further.
  validateSumTree = (
    originChart: ParentChartSumObj,
    answers: any,
    _nonChartSumGroupsDown: Map<string, number>,
    answerChanges: any[]
  ): Map<string, number> => {
    // Going down the tree, summing values in our section and passing it down.
    const treeSums = new Map<string, number>();
    const activeQuestionIds = new Set<number>();
    const totalsItemIds: Map<number, string> = new Map<number, string>();
    originChart.items.forEach((treeQuestionId) => {
      const docForm = this.idToContentItem.get(treeQuestionId) as PatientDocumentContentItem;
      if (
        docForm.isInValueGroup === true &&
        docForm.valueGroup !== undefined &&
        docForm.valueGroup !== null &&
        this.isDocItemTotalSumItem(docForm)
      ) {
        totalsItemIds.set(treeQuestionId, docForm.valueGroup);
      } else {
        this.getAnswerAndChildrenValue(treeQuestionId, treeSums, answers, activeQuestionIds);
      }
    });

    // Reaching a chart or another closed section, it handles the same way - goes down until everything is reached and then starts filling up
    // sums in their sections and passing their values up to us for summing into our totals.
    const childTreesGroupSums: Map<string, number>[] = [];
    originChart.children.forEach((childTree) => {
      const rows = JSON.parse(JSON.stringify(answers[childTree.parentId]));
      answers[childTree.parentId] = rows;
      if (
        activeQuestionIds.has(childTree.parentId) ||
        originChart.alwaysActiveCharts.has(childTree.parentId)
      ) {
        for (const rowNum of Object.keys(rows)) {
          const childTreeChanges: any[] = [];
          const childTreeSum = this.validateSumTree(
            childTree,
            rows[rowNum],
            treeSums,
            childTreeChanges
          );
          childTreeChanges.forEach((answer) => {
            answer.chartRow = parseInt(rowNum, 10);
            answerChanges.push(answer);
          });
          childTreesGroupSums.push(childTreeSum);
        }
      }
    });

    // Going back up.
    childTreesGroupSums.forEach((childTreeSums) => {
      this.combineAnswerMap(treeSums, childTreeSums);
    });

    originChart.sumItems.forEach((id: number) => {
      // const upperGroupSum = nonChartSumGroupsDown.get(group) || 0;

      // Applying our changes and reporting them back (though by reference, this is a bit cheaty).
      // Can do weird things by adding upperGroupSum.
      const content = this.idToContentItem.get(id);
      if (content !== undefined) {
        const group = content.valueGroup;
        if (group !== undefined && group !== null) {
          const groupAnswer = treeSums.get(group);
          const newAnswer = (groupAnswer !== undefined ? groupAnswer : 0).toString(10);
          answerChanges.push({ questionId: id, answer: newAnswer, chartRow: -1 });
          answers[id] = newAnswer;
        }
      }
    });

    return treeSums;
  };

  checkIfParentEqual(parentItemId: number, toData: string, answers?: any): boolean {
    const parentAnswer = this.getAnswer(parentItemId, answers);
    if (parentAnswer === null || parentAnswer === undefined) {
      return false;
    }
    if (parentAnswer instanceof Array) {
      for (const answer of parentAnswer) {
        if (typeof answer === "string" && answer.toLowerCase() === toData.toLowerCase()) {
          return true;
        }
      }
      return false;
    }

    return typeof parentAnswer === "string" && parentAnswer.toLowerCase() === toData.toLowerCase();
  }

  handleClickWhileDisabled() {
    if (this.state.disabledMessage !== null) {
      return;
    }

    this.setState({
      disabledMessage: translate(STRINGS.THIS_QUESTION_IS_READ_ONLY_YOU_ARE_NOT_ALLOWED_TO_ANSWER),
    });
  }

  renderQuestionErrorBottomSheet() {
    return (
      <AppOverlay>
        <QuestionErrorBottomSheet
          isOpen={this.state.disabledMessage !== null && this.state.disabledMessage !== ""}
          title={translate(STRINGS.OOPS)}
          errorMessage={this.state.disabledMessage}
          onRequestClose={() => this.setState({ disabledMessage: null })}
        />
      </AppOverlay>
    );
  }

  parseDocumentItem = (
    documnetReplaceConstants: DocumentReplaceConstants,
    rawPatientDocumentContentItem: PatientDocumentContentItem,
    idx: number
  ): JSX.Element | null => {
    const noChildrenQuestionTypes: DocumentItemQuestionType[] = [
      "chart",
      "customSignature",
      "medicationProfile",
      "icdCodes",
      "bloodPressure",
    ];
    replaceObjectTemplates(rawPatientDocumentContentItem, documnetReplaceConstants);
    const item = rawPatientDocumentContentItem;

    const answer = this.getAnswer(item.id);
    if (
      item.showIfParentEquals === true &&
      isDefined(item.ifParentEquals) &&
      isDefined(item.parentId) &&
      !this.checkIfParentEqual(item.parentId, item.ifParentEquals)
    ) {
      return null;
    }
    // basic is valid
    const isRequired = item.isRequired === undefined ? false : item.isRequired;
    const readonly = this.props.readonly === true || item.blockOnMobile === true;

    let isValid = this.state.showErrors
      ? isRequired
        ? answer !== null && answer !== ""
        : true
      : true;
    let currentItem: JSX.Element = <div>nothing</div>;
    switch (item.itemType) {
      case "radio":
        isValid = this.state.showErrors ? this.isValidRadioQuestion(item) : true;
        currentItem = (
          <RequiredDocRadio
            key={idx}
            questionId={item.id}
            questionText={item.label}
            prefilledAnswer={undefinedToNull(item.prefilledAnswer)}
            currentAnswer={answer}
            handleOnInsertAnswer={this.handleOnInsertAnswer}
            showOtherOption={item.showOtherOption as boolean}
            possibleAnswers={item.possibleAnswers as string[]}
            isRequired={isRequired}
            isValid={isValid}
            readonly={readonly}
            notes={item.notes === undefined ? null : item.notes}
            onClickWhileDisabled={() => this.handleClickWhileDisabled()}
          />
        );
        break;
      case "check":
        isValid = this.state.showErrors ? this.isValidCheckQuestion(item) : true;
        currentItem = (
          <RequiredDocCheck
            key={idx}
            questionId={item.id}
            questionText={item.label}
            prefilledAnswer={undefinedToNull(item.prefilledAnswer)}
            currentAnswer={answer}
            handleOnInsertAnswer={this.handleOnInsertAnswer}
            showOtherOption={item.showOtherOption as boolean}
            possibleAnswers={item.possibleAnswers as string[]}
            isRequired={isRequired}
            isValid={isValid}
            readonly={readonly}
            notes={item.notes === undefined ? null : item.notes}
            onClickWhileDisabled={() => this.handleClickWhileDisabled()}
          />
        );
        break;
      case "dropDown":
        currentItem = (
          <RequiredDocDropdownActivity
            key={idx}
            questionId={item.id}
            questionText={item.label}
            prefilledAnswer={undefinedToNull(item.prefilledAnswer)}
            currentAnswer={answer}
            handleOnInsertAnswer={this.handleOnInsertAnswer}
            showOtherOption={item.showOtherOption as boolean}
            possibleAnswers={item.possibleAnswers as string[]}
            isRequired={isRequired}
            isValid={isValid}
            readonly={readonly}
            notes={item.notes === undefined ? null : item.notes}
            onClickWhileDisabled={() => this.handleClickWhileDisabled()}
          />
        );
        break;
      case "yesNo":
        currentItem = (
          <RequiredDocYesNo
            key={idx}
            questionId={item.id}
            questionText={item.label}
            prefilledAnswer={undefinedToNull(item.prefilledAnswer)}
            currentAnswer={answer}
            handleOnInsertAnswer={this.handleOnInsertAnswer}
            isRequired={isRequired}
            isValid={isValid}
            readonly={readonly}
            notes={item.notes === undefined ? null : item.notes}
            onClickWhileDisabled={() => this.handleClickWhileDisabled()}
          />
        );
        break;
      case "bigHeader":
        currentItem = <RequiredDocMainHeader key={idx} text={item.label} />;
        break;
      case "smallHeader":
        currentItem = (
          <RequiredDocSubHeader
            key={idx}
            text={item.label}
            notes={item.notes === undefined ? null : item.notes}
          />
        );
        break;
      case "textShort":
        currentItem = (
          <RequiredDocShortText
            key={idx}
            questionId={item.id}
            handleOnInsertAnswer={this.handleOnInsertAnswer}
            questionText={item.label}
            prefilledAnswer={undefinedToNull(item.prefilledAnswer)}
            currentAnswer={answer}
            isRequired={isRequired}
            isValid={isValid}
            readonly={readonly}
            notes={item.notes === undefined ? null : item.notes}
            // exportableQuestions={this.props.exportableQuestions}
            // copyExportedQuestion={this.copyExportedQuestion}
            copyFromOtherDocs={item.copyFromOtherDocs}
            canCopyFromMedication={item.canCopyFromMedication}
            canCopyFromDisciplinAndTreatment={item.canCopyFromDisciplinAndTreatment}
            isIcd={item.isIcd}
            onClickWhileDisabled={() => this.handleClickWhileDisabled()}
          />
        );
        break;
      case "textLong":
        currentItem = (
          <RequiredDocLongText
            key={idx}
            questionId={item.id}
            currentAnswer={answer}
            handleOnInsertAnswer={this.handleOnInsertAnswer}
            questionText={item.label}
            prefilledAnswer={undefinedToNull(item.prefilledAnswer)}
            isRequired={isRequired}
            isValid={isValid}
            readonly={readonly}
            notes={item.notes === undefined ? null : item.notes}
            // exportableQuestions={this.props.exportableQuestions}
            // copyExportedQuestion={this.copyExportedQuestion}
            copyFromOtherDocs={item.copyFromOtherDocs}
            isIcd={item.isIcd}
            onClickWhileDisabled={() => this.handleClickWhileDisabled()}
          />
        );
        break;
      case "number":
        currentItem =
          item.isVital === true && item.nursingQuestionLinked !== undefined ? (
            <RequiredDocVitalSignActivity
              nursingQuestionId={item.nursingQuestionLinked}
              key={idx}
              questionId={item.id}
              currentAnswer={answer}
              questionText={item.label}
              prefilledAnswer={undefinedToNull(item.prefilledAnswer)}
              vitalType="REGULAR_NUMBER"
              readonly={item.blockOnMobile === true}
              handleOnInsertAnswer={this.handleOnInsertAnswer}
              isRequired={isRequired}
              isValid={isValid}
              notes={item.notes === undefined ? null : item.notes}
              vitalComments={item.vitalComments !== undefined ? item.vitalComments : null}
              vitalMeasuredAt={item.vitalMeasuredAt !== undefined ? item.vitalMeasuredAt : null}
              vitalMethod={item.vitalMethod !== undefined ? item.vitalMethod : null}
              vitalUnit={item.vitalUnit !== undefined ? item.vitalUnit : null}
            />
          ) : (
            <RequiredDocNumber
              key={idx}
              questionId={item.id}
              currentAnswer={answer}
              questionText={item.label}
              prefilledAnswer={undefinedToNull(item.prefilledAnswer)}
              handleOnInsertAnswer={this.handleOnInsertAnswer}
              isRequired={isRequired}
              isValid={isValid}
              readonly={readonly}
              notes={item.notes === undefined ? null : item.notes}
              onClickWhileDisabled={() => this.handleClickWhileDisabled()}
            />
          );
        break;
      case "employeeSignature":
        currentItem = (
          <RequiredDocSignature
            key={idx}
            questionId={item.id}
            questionText={item.label}
            prefilledAnswer={undefinedToNull(item.prefilledAnswer)}
            handleOnInsertAnswer={this.handleOnInsertAnswer}
            currentAnswer={answer}
            type={"Caregiver"}
            isRequired={isRequired}
            isValid={isValid}
            readonly={readonly}
            notes={item.notes === undefined ? null : item.notes}
            allowUnsigned={item.allowUnsignedSignature === true}
            onClickWhileDisabled={() => this.handleClickWhileDisabled()}
          />
        );
        break;
      case "patientSignature":
        currentItem = (
          <RequiredDocSignature
            key={idx}
            questionId={item.id}
            questionText={item.label}
            prefilledAnswer={undefinedToNull(item.prefilledAnswer)}
            handleOnInsertAnswer={this.handleOnInsertAnswer}
            currentAnswer={answer}
            type={"Patient"}
            isRequired={isRequired}
            isValid={isValid}
            readonly={readonly}
            notes={item.notes === undefined ? null : item.notes}
            allowUnsigned={item.allowUnsignedSignature === true ? true : false}
            onClickWhileDisabled={() => this.handleClickWhileDisabled()}
          />
        );
        break;
      case "time":
        currentItem = (
          <RequiredDocTime
            key={idx}
            questionId={item.id}
            questionText={item.label}
            prefilledAnswer={undefinedToNull(item.prefilledAnswer)}
            handleOnInsertAnswer={this.handleOnInsertAnswer}
            currentAnswer={answer}
            isRequired={isRequired}
            isValid={isValid}
            readonly={readonly}
            notes={item.notes === undefined ? null : item.notes}
            onClickWhileDisabled={() => this.handleClickWhileDisabled()}
          />
        );
        break;
      case "date":
        currentItem = (
          <RequiredDocDate
            key={idx}
            questionId={item.id}
            questionText={item.label}
            prefilledAnswer={undefinedToNull(item.prefilledAnswer)}
            handleOnInsertAnswer={this.handleOnInsertAnswer}
            currentAnswer={answer}
            isRequired={isRequired}
            isValid={isValid}
            readonly={readonly}
            notes={item.notes === undefined ? null : item.notes}
            onClickWhileDisabled={() => this.handleClickWhileDisabled()}
          />
        );
        break;
      case "editor":
        currentItem = <RequiredDocFreeText key={idx} htmlText={item.html as string} />;
        break;
      case "image":
        currentItem = (
          <RequiredDocImage key={idx} imgUrl={item.url as string} questionText={item.label} />
        );
        break;
      case "bodyDrawing":
        currentItem = (
          <BodyDrawing
            key={idx}
            readonly={readonly}
            currentItem={answer}
            handleOnInsertAnswer={(value: null | string | number | boolean | string[]) =>
              this.handleOnInsertAnswer(item.id, value)
            }
          />
        );
        break;
      case "chart":
        currentItem = (
          <RequiredDocChart
            key={idx}
            resubmissionReason={this.props.resubmissionReason}
            patientId={this.props.patientId}
            handleAnswers={this.silentHandleOnInsertAnswer}
            baseId={item.id}
            docFormat={(item.columns as [PatientDocumentContentItem[]])[0]}
            onBackClick={this.props.onBackClick}
            title={item.label}
            currentAnswers={answer}
            showErrors={this.state.showErrors}
            onAnswerQuestion={this.props.onAnswerQuestion}
            onSubmitForm={this.props.onSubmitForm}
            readonly={this.props.readonly}
            getIcdCodeAutoCompleteOptions={this.props.getIcdCodeAutoCompleteOptions}
            // Meaningless for charts.
            renderNewAnswers={this.renderNewAnswers}
            // exportableQuestions={this.props.exportableQuestions}
            // copyExportedQuestion={this.props.copyExportedQuestion}
            isScannable={this.props.isScannable}
            documentReplaceConstants={this.props.documentReplaceConstants}
            onScanUpload={this.props.onScanUpload}
            onValidateSubmissionDate={this.props.onValidateSubmissionDate}
            getScheduledDate={this.props.getScheduledDate}
            searchMedications={this.props.searchMedications}
            getPatientMedicationProfile={this.props.getPatientMedicationProfile}
            onCreateMedication={this.props.onCreateMedication}
            onUpdatedMedication={this.props.onUpdatedMedication}
            onDeleteMedication={this.props.onDeleteMedication}
            getPatientContractDiagnosisCodes={this.props.getPatientContractDiagnosisCodes}
            onCreateDiagnosis={this.props.onCreateDiagnosis}
            onEditDiagnosis={this.props.onEditDiagnosis}
            onDeleteDiagnosis={this.props.onDeleteDiagnosis}
            onClickWhileDisabled={() => this.handleClickWhileDisabled()}
            addNewPhysician={this.props.addNewPhysician}
            getAllPhysicians={this.props.getAllPhysicians}
            getPatientPrimaryPhysician={this.props.getPatientPrimaryPhysician}
            setPatientPhysician={this.props.setPatientPhysician}
            onClickAddPhysician={this.props.onClickAddPhysician}
            onCheckForOtherNursingQuestionsOnDocuments={
              this.props.onCheckForOtherNursingQuestionsOnDocuments
            }
          />
        );
        break;
      case "customSignature":
        currentItem = (
          <CustomSignature
            key={idx}
            currentAnswer={answer}
            getNameAnswer={this.getAnswer}
            handleOnInsertAnswer={this.handleOnInsertAnswer}
            isRequired={isRequired}
            isValid={isValid}
            item={item}
            notes={null}
            questionId={item.id}
            questionText={item.label}
            prefilledAnswer={undefinedToNull(item.prefilledAnswer)}
            readonly={readonly}
            showErrors={this.state.showErrors}
            onClickWhileDisabled={() => this.handleClickWhileDisabled()}
            hideName={item.hideNameFieldSignature === true}
          />
        );
        break;
      case "medicationProfile":
        currentItem = (
          <MedicationProfileDocumentSection
            key={idx}
            item={item}
            isDisabled={this.props.readonly}
            required={item.isRequired ?? false}
            showErrors={this.state.showErrors}
            searchMedications={this.props.searchMedications}
            getPatientMedicationProfile={this.props.getPatientMedicationProfile}
            onCreateMedication={this.props.onCreateMedication}
            onUpdatedMedication={this.props.onUpdatedMedication}
            onDeleteMedication={this.props.onDeleteMedication}
            onInsertAnswer={this.handleOnInsertAnswer}
          />
        );
        break;
      case "medicationProfileItem":
        return <>{/* related to medicationProfile as a child only */}</>;
      case "icd":
      case "icdChart":
        return <>{/* @deprecated but still here for older documents compatibility */}</>;
      case "icdCodes":
        currentItem = (
          <PatientContractDiagnosisCodeActivities
            key={idx}
            questionId={item.id}
            currentAnswer={answer}
            isDisabled={this.props.readonly}
            getIcdCodeAutoCompleteOptions={this.props.getIcdCodeAutoCompleteOptions}
            getPatientContractDiagnosisCodes={this.props.getPatientContractDiagnosisCodes}
            onCreateDiagnosis={this.props.onCreateDiagnosis}
            onUpdateDiagnosis={this.props.onEditDiagnosis}
            onDeleteDiagnosis={this.props.onDeleteDiagnosis}
            onInsertAnswer={this.handleOnInsertAnswer}
          />
        );
        break;
      case "bloodPressure":
        currentItem =
          item.isVital === true && item.nursingQuestionLinked !== undefined ? (
            <RequiredDocVitalSignActivity
              nursingQuestionId={item.nursingQuestionLinked}
              key={idx}
              questionId={item.id}
              currentAnswer={answer}
              questionText={item.label}
              prefilledAnswer={undefinedToNull(item.prefilledAnswer)}
              vitalType="BLOOD_PRESSURE"
              readonly={item.blockOnMobile === true}
              handleOnInsertAnswer={this.handleOnInsertAnswer}
              isRequired={isRequired}
              isValid={isValid}
              notes={item.notes === undefined ? null : item.notes}
              vitalComments={item.vitalComments !== undefined ? item.vitalComments : null}
              vitalMeasuredAt={item.vitalMeasuredAt !== undefined ? item.vitalMeasuredAt : null}
              vitalMethod={item.vitalMethod !== undefined ? item.vitalMethod : null}
              vitalUnit={item.vitalUnit !== undefined ? item.vitalUnit : null}
            />
          ) : (
            <RequiredDocBloodPressure
              key={idx}
              questionId={item.id}
              questionText={item.label}
              prefilledAnswer={undefinedToNull(item.prefilledAnswer)}
              handleOnInsertAnswer={this.handleOnInsertAnswer}
              firstNumber={
                answer === undefined || answer === null || typeof answer === "string"
                  ? null
                  : answer[0]
              }
              secondNumber={
                answer === undefined || answer === null || typeof answer === "string"
                  ? null
                  : answer[1]
              }
              isRequired={isRequired}
              isValid={isValid}
              readonly={readonly}
              notes={item.notes === undefined ? null : item.notes}
              onClickWhileDisabled={() => this.handleClickWhileDisabled()}
            />
          );
        break;
      case "patientPhysician":
        currentItem = (
          <PatientPhysicianDetails
            isReadOnly={readonly}
            getAllPhysicians={this.props.getAllPhysicians}
            setPatientPhysician={this.props.setPatientPhysician}
            addNewPhysician={this.props.addNewPhysician}
            getPatientPrimaryPhysician={this.props.getPatientPrimaryPhysician}
            key={idx}
            patientId={this.props.patientId}
            onClickAddNewPhysician={this.props.onClickAddPhysician}
          />
        );
        break;
      case "POC":
        isValid = this.state.showErrors ? this.isValidPOCQuestion(item) : true;
        currentItem = (
          <POCDocumentQuestion
            key={idx}
            questionId={item.id}
            questionText={item.label}
            currentAnswer={answer}
            onInsertAnswer={this.handleOnInsertAnswer}
            POCItems={item.POCItems as PatientDocumentPOCItem[]}
            isRequired={isRequired}
            isValid={isValid}
            readonly={readonly}
            onClickWhileDisabled={() => this.handleClickWhileDisabled()}
          />
        );
        break;
      default:
        return null;
    }

    let subChildren: (JSX.Element | null)[] = [];
    if (
      !noChildrenQuestionTypes.includes(item.itemType) &&
      item.columns !== null &&
      item.columns !== undefined &&
      item.columns[0].length > 0
    ) {
      subChildren = item.columns[0].map((v, index) =>
        this.parseDocumentItem(documnetReplaceConstants, v, index)
      );
    }

    return (
      <div className="document-question" key={idx}>
        {currentItem}
        {subChildren}
      </div>
    );
  };

  validateInAnswers = (item: PatientDocumentContentItem, answers: any): number[] => {
    const answer = this.getAnswer(item.id, answers);
    if (
      item.showIfParentEquals === true &&
      isDefined(item.parentId) &&
      isDefined(item.ifParentEquals) &&
      !this.checkIfParentEqual(item.parentId, item.ifParentEquals, answers)
    ) {
      return [];
    }

    // basic is valid
    const isRequired = item.isRequired === undefined ? false : item.isRequired;

    let isValid = isRequired ? answer !== null && answer !== "" : true;
    switch (item.itemType) {
      case "check":
        isValid = this.isValidCheckQuestion(item);
        break;
      case "radio":
        isValid = this.isValidRadioQuestion(item);
        break;
      case "POC":
        isValid = this.isValidPOCQuestion(item);
        break;
      case "bodyDrawing":
        isValid = isRequired
          ? answer !== null &&
            (answer as string[]).length > 0 &&
            (answer as string[]).indexOf("") === -1
          : true;
        break;
      case "dropDown":
      case "yesNo":
      case "textShort":
      case "textLong":
      case "number":
      case "employeeSignature":
      case "patientSignature":
      case "customSignature":
      case "time":
      case "bloodPressure":
      case "date":
        // do nothing
        break;
      case "bigHeader":
      case "smallHeader":
      case "editor":
      case "image":
      case "medicationProfile":
      case "medicationProfileItem":
      case "icd":
      case "icdChart":
      case "icdCodes":
      case "patientPhysician":
        // always valid
        break;
      case "chart": {
        let invalid: number[] = [];
        for (const row of Object.keys(answer)) {
          invalid = invalid.concat(
            this.validateDocs((item.columns as [PatientDocumentContentItem[]])[0], answer[row])
          );
        }
        return invalid;
      }
      default:
        assertNever(item.itemType);
    }

    let invalids = isValid ? [] : [item.id];

    if (item.columns !== undefined && item.columns !== null && item.columns[0].length > 0) {
      item.columns[0].forEach((val) => {
        const columnInvalids = this.validateInAnswers(val, answers);
        invalids = invalids.concat(columnInvalids);
      });
    }

    return invalids;
  };

  validateDocs = (docItems: PatientDocumentContentItem[], answers: any): number[] => {
    // TODO These any casts are troubling... they are likely incorrect
    const required = docItems.reduce(
      (
        acc: PatientDocumentContentItem,
        val: PatientDocumentContentItem
      ): PatientDocumentContentItem => {
        const newThing = this.validateInAnswers(val, answers);

        return [...(acc as any), ...newThing] as any;
      },
      [] as any
    );
    return required as any;
  };

  storePrefilledAnswers = async () => {
    const savePrefilled = [];

    const isAnswerDefined = (questionId: number) => {
      const answer = this.getAnswer(questionId, this.state.answers);
      return isDefined(answer);
    };

    for (const question of this.props.docFormat) {
      if (isDefined(question.prefilledAnswer)) {
        if (!isAnswerDefined(question.id)) {
          savePrefilled.push(this.handleOnInsertAnswer(question.id, question.prefilledAnswer));
        }
      } else if (question.itemType === "icdCodes") {
        if (!isAnswerDefined(question.id)) {
          const diagnosesData = await this.props.getPatientContractDiagnosisCodes();
          savePrefilled.push(
            this.handleOnInsertAnswer(question.id, diagnosesData.patientContractDiagnosisCodes)
          );
        }
      }
    }

    return Promise.all(savePrefilled);
  };

  isValidCheckQuestion = (item: PatientDocumentContentItem) => {
    const answer = this.getAnswer(item.id) as string[];
    let isValid = item.isRequired === true ? isDefined(answer) && answer.length > 0 : true;

    if (isValid) {
      const isFalsyValue = (val: string) => !isDefined(val) || val === "";
      isValid = answer.filter(isFalsyValue).length === 0;
    }

    return isValid;
  };

  isValidRadioQuestion = (item: PatientDocumentContentItem) => {
    const answer = this.getAnswer(item.id) as string;
    let isValid = item.isRequired === true ? isDefined(answer) : true;

    if (isValid) {
      isValid = answer !== "";
    }

    return isValid;
  };

  isValidPOCQuestion = (item: PatientDocumentContentItem) => {
    const answer = this.getAnswer(item.id) as POCItemsDocumentAnswer[] | null | undefined;
    return item.isRequired === true ? isDefined(answer) && answer.length > 0 : true;
  };

  isMedicatipnProfileInvalid = async (): Promise<boolean> => {
    const requiredMedicationProfile = this.props.docFormat.find(
      (x) => x.itemType === "medicationProfile" && x.isRequired === true
    );

    if (requiredMedicationProfile === undefined) {
      return false;
    }
    try {
      const { medications } = await this.props.getPatientMedicationProfile();
      return medications.length === 0;
    } catch (error) {
      console.error("Could not fetch medication Profile, error:", error);
      return false;
    }
  };

  validate = async () => {
    await this.storePrefilledAnswers();

    const invalidAnswers = this.validateDocs(this.props.docFormat, this.state.answers);
    const isMedicationProfileInvalid = await this.isMedicatipnProfileInvalid();
    if (invalidAnswers.length > 0 || isMedicationProfileInvalid) {
      this.setState(
        {
          ...this.state,
          showErrors: true,
          showRequiredFieldsDialog: true,
        },
        this.onInvalidAnswers.bind(this)
      );
      return;
    }
    if (!this.props.onValidateSubmissionDate()) {
      this.setState({
        ...this.state,
        isSubmissionMissmatchOpen: true,
      });
      return;
    } else {
      const { documentsToBeRegenerated } =
        await this.props.onCheckForOtherNursingQuestionsOnDocuments();
      if (documentsToBeRegenerated.length > 0) {
        this.setState({
          ...this.state,
          documentsToBeRegenerated,
          isSubmissionMissmatchOpen: false,
          isRegenerateOtherDocumentsModalOpen: true,
        });
      } else {
        this.handleSubmitForm({
          submissionDate: null,
          regenerateNursingQuestionsOtherDocuments: false,
        });
      }
    }
  };

  /**
   * Handle logic after the form is being marked as invalid
   */
  onInvalidAnswers(): void {
    this.showWarningAlert();
    this.scrollToFirstInvalidField();
  }

  /**
   * Present a warning alert that notifies the client of missing fields.
   */
  private showWarningAlert(): void {
    simpleAlert({
      message: this.getAlertMessage(),
      icon: "warning",
    });
  }

  /**
   * Scroll to the first field that marked as invalid.
   */
  private scrollToFirstInvalidField(): void {
    const element = document.querySelector(".ScheduledVisitDocScreen__Scaffold .AppCard.__invalid");

    if (element !== null) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  }

  getAlertMessage = () => {
    if (this.state.showRequiredFieldsDialog) {
      return translate(STRINGS.REQUIRED_FIELDS_MISSING_IN_PATIENT_DOC);
    } else if (this.state.showSubmitError) {
      return translate(STRINGS.PATIENT_DOC_NOT_SUBMITTED);
    } else {
      return "";
    }
  };

  renderNewAnswers = (newAnswers: PatientDocumentAnswerObject[]) => {
    const newAnswerState = getEmptyFormat(this.props.docFormat);
    this.initAnswers(newAnswers, newAnswerState, false);
    this.setState({
      ...this.state,
      answers: newAnswerState,
    });
  };

  copyFromLastTime = (newAnswers: PatientDocumentAnswerObject[]) => {
    if (newAnswers.length > 0) {
      const mostRecentDate = newAnswers.reduce((previousDate: Instant | null, ans) => {
        if (ans.answeredAt !== null) {
          if (previousDate === null || ans.answeredAt > previousDate) {
            return ans.answeredAt;
          }
        }
        return previousDate;
      }, null);
      const lastAnswerDisplay =
        mostRecentDate !== null
          ? globalize().formatLocalDate(LocalDate.ofInstant(mostRecentDate), { date: "short" })
          : translate(STRINGS.COPY_LAST_NO_DATE_FALLBACK_APPENDED);

      ons.notification
        .confirm({
          animation: "none",
          modifier: "material",
          title: translate(STRINGS.CONFIRM_COPY_LAST_TITLE),
          buttonLabels: [translate(STRINGS.NO), translate(STRINGS.YES)] as any,
          message: translate(STRINGS.CONFIRM_COPY_LAST) + " " + lastAnswerDisplay + "?",
        })
        .then((result) => {
          // According to docs:
          // Returns: Will resolve to the index of the button that was pressed or -1 when canceled.
          // Since we put two button labels and no first, we'll take 0 as cancel.
          if ((result as any) !== 0) {
            const newAnswerState = getEmptyFormat(this.props.docFormat);
            this.initAnswers(newAnswers, newAnswerState, false);
            this.setState({
              ...this.state,
              answers: newAnswerState,
            });
          }
        });
    } else {
      ons.notification.alert({
        animation: "none",
        modifier: "material warning",
        title: translate(STRINGS.NO_ANSWERS_TO_COPY_TITLE),
        message: translate(STRINGS.NO_ANSWERS_TO_COPY),
      });
    }
  };

  onClickScan() {
    this.setState({ isScanningNewDocument: true });
  }

  scanSuccessCallback = (data: PatientDocumentScanCreate) => {
    this.props.onScanUpload(
      data.base64,
      () => this.setState({ submitting: true }),
      () => this.setState({ submitting: false })
    );
  };

  scanErrorCallback = (message: string) => {
    alert("Failed because: " + message);
    ons.notification.alert({
      animation: "none",
      modifier: "material",
      title: translate(STRINGS.ERROR),
      message: translate(STRINGS.UPLOAD_ERROR),
      cancelable: true,
    });
  };

  hideDateMissmatchPopup = () => {
    this.setState({
      ...this.state,
      isSubmissionMissmatchOpen: false,
    });
  };

  hideRegenerateOtherDocumentsModal = () => {
    this.setState({
      ...this.state,
      isRegenerateOtherDocumentsModalOpen: false,
    });
  };

  updateNewSubmissionDate = async (
    documentSubmissionDate: DocumentSubmissionDate,
    date?: LocalDate | undefined
  ) => {
    switch (documentSubmissionDate) {
      case DocumentSubmissionDate.TODAY: {
        this.setState({
          ...this.state,
          submitDate: LocalDate.now(),
        });
        break;
      }
      case DocumentSubmissionDate.SCHEDULED_DATE: {
        const scheduledDate = this.props.getScheduledDate();
        if (scheduledDate === null) {
          return;
        } else {
          this.setState({
            ...this.state,
            submitDate: scheduledDate,
          });
          break;
        }
      }
      case DocumentSubmissionDate.DATE_PICKER_DATE: {
        if (date === undefined) {
          break;
        } else {
          this.setState({
            ...this.state,
            submitDate: date,
          });
          break;
        }
      }
      default:
        return assertNever(documentSubmissionDate);
    }

    const { documentsToBeRegenerated } =
      await this.props.onCheckForOtherNursingQuestionsOnDocuments();
    if (documentsToBeRegenerated.length > 0) {
      this.setState({
        ...this.state,
        isRegenerateOtherDocumentsModalOpen: true,
        isSubmissionMissmatchOpen: false,
        documentsToBeRegenerated,
      });
      return;
    } else {
      this.submitPatientDocumentAndCloseModals();
      return;
    }
  };

  submitPatientDocumentAndCloseModals() {
    this.setState(
      {
        ...this.state,
        isSubmissionMissmatchOpen: false,
        isRegenerateOtherDocumentsModalOpen: false,
      },
      () =>
        this.handleSubmitForm({
          submissionDate: this.state.submitDate,
          regenerateNursingQuestionsOtherDocuments: this.state.regenerateOtherDocuments,
        })
    );
  }

  renderSubmissionMissmatchModal() {
    const scheduledDate = this.props.getScheduledDate();

    return (
      <AppModal
        type="info"
        isOpen={this.state.isSubmissionMissmatchOpen}
        title={translate(STRINGS.DATE_VERIFICATION)}
        message={translate(STRINGS.PATIENT_DOC_SUBMISSION_MISSMATCH)}
        actions={[
          {
            key: "submit-with-today-date",
            variant: "filled",
            size: "default",
            color: "primary",
            onClick: () => this.updateNewSubmissionDate(DocumentSubmissionDate.TODAY),
            children:
              translate(STRINGS.TODAYS_DATE) +
              ": " +
              globalize().formatLocalDate(LocalDate.now(), { date: "short" }),
          },
          {
            key: "submit-with-scheduled-date",
            variant: "soft",
            size: "default",
            color: "black",
            onClick: () => this.updateNewSubmissionDate(DocumentSubmissionDate.SCHEDULED_DATE),
            children:
              translate(STRINGS.SCHEDULED_DATE) +
              (scheduledDate !== null
                ? ": " + globalize().formatLocalDate(scheduledDate, { date: "short" })
                : null),
          },
          {
            key: "submit-with-chosen-date",
            variant: "link",
            size: "default",
            color: "black",
            onClick: () =>
              openDatepicker(
                {
                  type: "date",
                  maxDate: LocalDate.now(),
                  callbackObj: {
                    callback: (data) =>
                      this.updateNewSubmissionDate(DocumentSubmissionDate.DATE_PICKER_DATE, data),
                    cancelable: true,
                  },
                },
                translate(STRINGS.SELECT_DATE)
              ),
            children: translate(STRINGS.CHOOSE_ANOTHER_DATE),
          },
        ]}
        onRequestClose={this.hideDateMissmatchPopup}
      />
    );
  }

  renderRegenerateOtherDocumentsModal() {
    return (
      <AppModal
        type="info"
        isOpen={this.state.isRegenerateOtherDocumentsModalOpen}
        title={translate(STRINGS.SOME_QUESTIONS_CHANGED_APPEARS_ON_OTHER_DOCUMENTS)}
        message={translate(
          STRINGS.SOME_QUESTIONS_YOU_CHANGED_APPEARS_IN_X_DOCUMENTS_SHOULD_WE_UPDATE_THEM
        )
          .replace("{{documents_count}}", this.state.documentsToBeRegenerated.length.toString())
          .replace("{{documents_name}}", this.state.documentsToBeRegenerated.join(", "))}
        actions={[
          {
            key: "Yes",
            variant: "filled",
            size: "default",
            color: "primary",
            onClick: () => {
              this.setState(
                {
                  ...this.state,
                  regenerateOtherDocuments: true,
                },
                () => this.submitPatientDocumentAndCloseModals()
              );
            },
            children: translate(STRINGS.YES_UPDATE_ALL),
          },
          {
            key: "No",
            variant: "soft",
            size: "default",
            color: "black",
            onClick: () => {
              this.setState(
                {
                  ...this.state,
                  regenerateOtherDocuments: false,
                },
                () => this.submitPatientDocumentAndCloseModals()
              );
            },
            children: translate(STRINGS.NO_JUST_THIS_ONE),
          },
        ]}
        onRequestClose={this.hideRegenerateOtherDocumentsModal}
      />
    );
  }

  handleSubmitForm({
    regenerateNursingQuestionsOtherDocuments,
    submissionDate,
  }: {
    submissionDate: LocalDate | null;
    regenerateNursingQuestionsOtherDocuments: boolean;
  }) {
    this.setState({
      ...this.state,
      submitting: true,
    });
    this.props
      .onSubmitForm(submissionDate, regenerateNursingQuestionsOtherDocuments)
      .catch((error) => {
        const disabledMessage = parseErrorResponse(error);
        this.setState({
          ...this.state,
          submitting: false,
          disabledMessage: disabledMessage,
        });
      });
  }

  render() {
    const headerElem = document.querySelector(".ScreenSkeleton__header");

    const form = (
      <>
        {this.props.resubmissionReason != null && (
          <ResubmissionBanner resubmissionReason={this.props.resubmissionReason} />
        )}
        {this.props.readonly === false && this.props.getLastAnswers !== undefined && (
          <CopyFromLastTimeButton
            getLastAnswers={this.props.getLastAnswers}
            onError={() => {
              ons.notification.alert({
                animation: "none",
                modifier: "material",
                title: translate(STRINGS.ERROR),
                message: translate(STRINGS.SOMETHING_WENT_WRONG),
                cancelable: true,
              });
            }}
            onRecievedAnswers={this.copyFromLastTime}
          />
        )}
        {this.props.docFormat.map((val, idx) =>
          this.parseDocumentItem(this.props.documentReplaceConstants, val, idx)
        )}
        {this.props.readonly === false && (
          <div className="ScheduledVisitDocScreen__margin ScheduledVisitDocScreen__center">
            {this.state.submitting || !this.state.canSubmit ? (
              <Ons.ProgressCircular indeterminate={true} />
            ) : (
              <AppButton
                block={true}
                onClick={() => this.validate()}
                disabled={this.state.submitDisabled}
              >
                {translate(STRINGS.SUBMIT)}
              </AppButton>
            )}
          </div>
        )}
      </>
    );

    return (
      <Scaffold
        className="ScheduledVisitDocScreen__Scaffold"
        title={<ScaffoldTitle label={this.props.title} />}
        onClickGoBack={this.props.onBackClick}
      >
        <style>
          {headerElem !== null
            ? `
                             .ScheduledVisitDocScreen__documentChangesSaved {
                                top: ${headerElem.clientHeight}px !important;
                             }`
            : null}
        </style>

        <div className="ScheduledVisitDocScreen__document">
          <FormContainer
            isScannable={this.props.isScannable}
            isReadOnly={this.props.readonly}
            form={form}
            uploadFileButton={<UploadFileButton onConfirm={() => this.onClickScan()} />}
          />
        </div>
        {this.renderSubmissionMissmatchModal()}
        {this.renderQuestionErrorBottomSheet()}
        {this.renderRegenerateOtherDocumentsModal()}
        <DocumentUploadActionsActivity
          isDocumentNameNeeded={false}
          isScanningNewDocument={this.state.isScanningNewDocument}
          onScanSuccessCallback={this.scanSuccessCallback}
          onScanErrorCallback={this.scanErrorCallback}
        />
      </Scaffold>
    );
  }
}

function UploadFileButton(props: { onConfirm: () => void }) {
  const modals = useModalV2();

  const handleClick = () => {
    modals.createModal({
      type: "info",
      title: translate(STRINGS.OVERRIDE_FORM),
      message: translate(STRINGS.OVERRIDE_FORM_MESSAGE),
      actions: [
        ({ close }) => ({
          key: "confirm",
          variant: "filled",
          size: "default",
          color: "primary",
          onClick: () => {
            close();
            props.onConfirm();
          },
          children: translate(STRINGS.YES_OVERRIDE_FORM),
        }),
        ({ close }) => ({
          key: "cancel",
          variant: "soft",
          size: "default",
          color: "black",
          onClick: close,
          children: translate(STRINGS.NO_TAKE_ME_BACK),
        }),
      ],
    });
  };

  return (
    <AppButton variant="filled" block color="primary" onClick={handleClick}>
      {CAMERA_SVG}
      <span>{translate(STRINGS.UPLOAD_DOCUMENT)}</span>
    </AppButton>
  );
}

function CopyFromLastTimeButton(props: {
  getLastAnswers: () => Promise<PatientDocumentAnswers>;
  onRecievedAnswers: (answers: PatientDocumentAnswerObject[]) => void;
  onError: () => void;
}) {
  const copyFromLastTime = useMutation({
    mutationFn: props.getLastAnswers,
    onSuccess: ({ answers }) => props.onRecievedAnswers(answers),
    onError: props.onError,
  });

  return (
    <AppButton
      variant="soft"
      block={true}
      isLoading={copyFromLastTime.isLoading}
      onClick={() => copyFromLastTime.mutate()}
    >
      {COPY_SVG}
      <span>{translate(STRINGS.COPY_FROM_LAST_TIME)}</span>
    </AppButton>
  );
}

function FormContainer(props: {
  isScannable: boolean;
  isReadOnly: boolean;
  form: React.ReactNode;
  uploadFileButton: React.ReactNode;
}) {
  const [submissionType, setSubmissionType] = React.useState<"form" | "file">("form");

  if (props.isReadOnly || !props.isScannable) {
    return <>{props.form}</>;
  }

  return (
    <>
      <AppCard>
        <AppCardHeader>
          {submissionType === "form" ? (
            <>
              <AppCardTitle>
                {translate(STRINGS.FILL_IN_THE_FORM_TO_SUBMIT_THE_DOCUMENT)}.
              </AppCardTitle>
              <SubHeading>
                <span>{translate(STRINGS.TO_UPLOAD_A_FILE_INSTEAD)}</span>

                <AppButton variant="link" onClick={() => setSubmissionType("file")}>
                  {translate(STRINGS.CLICK_HERE)}.
                </AppButton>
              </SubHeading>
            </>
          ) : (
            <>
              <AppCardTitle>{translate(STRINGS.UPLOAD_A_DOCUMENT)}.</AppCardTitle>
              <SubHeading>
                <span>{translate(STRINGS.TO_FILL_IN_A_FORM_INSTEAD)}</span>

                <AppButton variant="link" onClick={() => setSubmissionType("form")}>
                  {translate(STRINGS.CLICK_HERE)}.
                </AppButton>
              </SubHeading>
            </>
          )}
        </AppCardHeader>
      </AppCard>
      <FormWrapper>
        {submissionType === "form" && <>{props.form}</>}
        {submissionType === "file" && <>{props.uploadFileButton}</>}
      </FormWrapper>
    </>
  );
}

const FormWrapper = styled.div`
  margin-top: 1rem;
`;

const SubHeading = styled.p`
  font-size: 14px;
  font-weight: 400;
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0;

  button {
    padding-inline-start: 0;
  }
`;

interface ChartInfo extends ScheduledVisitDocScreenProps {
  baseId: number;
  handleAnswers: (id: number, value: any) => void;
  renderNewAnswers: (docs: PatientDocumentAnswerObject[]) => void;
  currentAnswers: any;
  showErrors: boolean;
  documentReplaceConstants: DocumentReplaceConstants;
  onClickWhileDisabled?: () => void;
}
class RequiredDocChart extends React.Component<ChartInfo, any> {
  constructor(props: ChartInfo) {
    super(props);
  }

  shouldComponentUpdate(nextProps: ChartInfo, nextState: any) {
    return !deepEqual(nextProps, this.props) || !deepEqual(nextState, this.state);
  }

  updateParent = (rowNum: string, value: any) => {
    const newAnswers = {
      ...this.props.currentAnswers,
      [rowNum]: {
        ...value,
      },
    };
    this.props.handleAnswers(this.props.baseId, newAnswers);
  };

  renderAnswers = (): JSX.Element[] => {
    const map = [];

    for (const rowNum in this.props.currentAnswers) {
      map.push(
        <div key={rowNum}>
          <RequiredDocChartRow
            resubmissionReason={this.props.resubmissionReason}
            patientId={this.props.patientId}
            updateParent={this.updateParent}
            currentAnswers={this.props.currentAnswers[rowNum]}
            baseId={this.props.baseId}
            docFormat={this.props.docFormat}
            handleAnswers={this.props.handleAnswers}
            onBackClick={this.props.onBackClick}
            rowNum={rowNum}
            title={this.props.title}
            showErrors={this.props.showErrors}
            onAnswerQuestion={this.props.onAnswerQuestion}
            onSubmitForm={this.props.onSubmitForm}
            readonly={this.props.readonly}
            skipChartTree={true}
            getIcdCodeAutoCompleteOptions={this.props.getIcdCodeAutoCompleteOptions}
            renderNewAnswers={this.props.renderNewAnswers}
            // exportableQuestions={this.props.exportableQuestions}
            // copyExportedQuestion={this.props.copyExportedQuestion}
            isScannable={this.props.isScannable}
            documentReplaceConstants={this.props.documentReplaceConstants}
            onScanUpload={this.props.onScanUpload}
            onValidateSubmissionDate={this.props.onValidateSubmissionDate}
            getScheduledDate={this.props.getScheduledDate}
            searchMedications={this.props.searchMedications}
            getPatientMedicationProfile={this.props.getPatientMedicationProfile}
            onCreateMedication={this.props.onCreateMedication}
            onUpdatedMedication={this.props.onUpdatedMedication}
            onDeleteMedication={this.props.onDeleteMedication}
            getPatientContractDiagnosisCodes={this.props.getPatientContractDiagnosisCodes}
            onCreateDiagnosis={this.props.onCreateDiagnosis}
            onEditDiagnosis={this.props.onEditDiagnosis}
            onDeleteDiagnosis={this.props.onDeleteDiagnosis}
            onClickWhileDisabled={() =>
              this.props.readonly ? null : this.props.onClickWhileDisabled
            }
            addNewPhysician={this.props.addNewPhysician}
            getAllPhysicians={this.props.getAllPhysicians}
            getPatientPrimaryPhysician={this.props.getPatientPrimaryPhysician}
            setPatientPhysician={this.props.setPatientPhysician}
            onClickAddPhysician={this.props.onClickAddPhysician}
            onCheckForOtherNursingQuestionsOnDocuments={
              this.props.onCheckForOtherNursingQuestionsOnDocuments
            }
          />
        </div>
      );
    }
    return map;
  };

  notifyOnRemovingRow(row: any, rowNum: number) {
    for (const id of Object.keys(row)) {
      if (!(row[id] instanceof Array) && row[id] instanceof Object) {
        Object.keys(row[id]).forEach((val) => {
          this.notifyOnRemovingRow(row[id][val], parseInt(val, 10));
        });
      } else {
        this.props.onAnswerQuestion(
          parseInt(id, 10),
          rowNum,
          null,
          () => showDocumentSaved(),
          Instant.now()
        );
      }
    }
  }

  removeRow = (rowNum?: string) => {
    // TODO I think it might be better for `newAnswers` to be a Map here
    const newAnswers = {
      ...this.props.currentAnswers,
    };
    const row = rowNum === undefined ? this.getLatestRow() : parseInt(rowNum, 10);

    if (newAnswers[row] === undefined) {
      return;
    }
    const rowToDelete = newAnswers[row];

    this.notifyOnRemovingRow(rowToDelete, row);

    // tslint:disable-next-line:no-dynamic-delete
    delete newAnswers[row];

    // TODO: remove all the questions of this row
    this.props.handleAnswers(this.props.baseId, newAnswers);
  };

  getLatestRow() {
    const keys = Object.keys(this.props.currentAnswers);
    if (keys.length === 0) {
      return 0;
    }
    const latest = parseInt(keys[keys.length - 1], 10);
    return latest;
  }

  addRows = (amount: number) => {
    const rowNum = this.getLatestRow() + 1;
    const newAnswers = {
      ...this.props.currentAnswers,
    };

    for (let i = 0; i < amount; i++) {
      newAnswers[rowNum + i] = getEmptyFormat(this.props.docFormat);
    }

    this.props.handleAnswers(this.props.baseId, newAnswers);
  };

  handleAppCardClick() {
    if (this.props.readonly && this.props.onClickWhileDisabled !== undefined) {
      this.props.onClickWhileDisabled();
    }
  }

  render() {
    return (
      <AppCard className="document-question">
        <QuestionHeader
          text={this.props.title}
          isRequired={false}
          notes={null}
          isReadonly={this.props.readonly}
          onClick={() => this.handleAppCardClick()}
        />

        <AppCardContent>{this.renderAnswers()}</AppCardContent>

        {!this.props.readonly ? (
          <AppCardContent>
            <div className="ScheduledVisitDocScreen__chartRemoveButton">
              <CardButton
                onClick={() => this.removeRow()}
                color="danger"
                variation="outline"
                style={{ marginRight: "5px" }}
              >
                {translate(STRINGS.REMOVE)}
              </CardButton>
              <CardButton onClick={() => this.addRows(1)} variation="filled">
                {translate(STRINGS.ADD)}
              </CardButton>
            </div>
          </AppCardContent>
        ) : (
          ""
        )}
      </AppCard>
    );
  }
}

interface ChartRowInfo extends ChartInfo {
  //
  rowNum: string;
  updateParent: (rowNum: string, value: any) => void;
  showErrors: boolean;
  documentReplaceConstants: DocumentReplaceConstants;
}

class RequiredDocChartRow extends ScheduledVisitDocScreen<ChartRowInfo> {
  constructor(props: ChartRowInfo) {
    super(props);
    this.state = {
      answers: props.currentAnswers,
      showErrors: props.showErrors,
      showRequiredFieldsDialog: false,
      submitting: false,
      showSubmitError: false,
      canSubmit: true,
      isSubmissionMissmatchOpen: false,
      disabledMessage: null,
      isScanningNewDocument: false,
      submitDisabled: false,
      isRegenerateOtherDocumentsModalOpen: false,
      regenerateOtherDocuments: false,
      submitDate: null,
      documentsToBeRegenerated: [],
    };
  }

  handleOnInsertAnswer = (id: number, value: any, vitalExtraDetails?: VitalExtraDetails) => {
    this.silentHandleOnInsertAnswer(id, value);
    return this.props.onAnswerQuestion(
      id,
      parseInt(this.props.rowNum, 10),
      value,
      () => showDocumentSaved(),
      Instant.now(),
      vitalExtraDetails
    );
  };

  silentHandleOnInsertAnswer = (id: number, value: any) => {
    const answers = {
      ...this.state.answers,
    };
    answers[id] = value;
    this.setState({
      ...this.state,
      answers: answers,
    });
    this.props.updateParent(this.props.rowNum, answers);
  };

  shouldComponentUpdate(nextProps: ChartRowInfo, nextState: any) {
    if (!deepEqual(nextProps.currentAnswers, this.props.currentAnswers)) {
      this.setState({
        ...this.state,
        answers: nextProps.currentAnswers,
      });
      return true;
    } else if (!deepEqual(nextProps.showErrors, this.props.showErrors)) {
      this.setState({
        ...this.state,
        showErrors: nextProps.showErrors,
      });
      return true;
    } else {
      return !deepEqual(this.state, nextState);
    }
  }

  render() {
    return (
      <div
        key={this.props.rowNum}
        onClick={() =>
          this.props.onClickWhileDisabled !== undefined ? this.props.onClickWhileDisabled() : null
        }
      >
        {this.props.docFormat.map((val, idx) =>
          this.parseDocumentItem(this.props.documentReplaceConstants, val, idx)
        )}
      </div>
    );
  }
}

function getEmptyFormat(items: PatientDocumentContentItem[], emptyChart?: boolean) {
  let answers: any = {};
  items.forEach((val) => {
    if (val.columns !== undefined && val.columns !== null) {
      if (val.itemType === "chart") {
        if (emptyChart === true) {
          answers = {
            ...answers,
            [val.id]: {},
          };
        } else {
          answers = {
            ...answers,
            [val.id]: {
              0: {
                ...getEmptyFormat(val.columns[0]),
              },
            },
          };
        }
        return;
      } else {
        answers = {
          ...answers,
          ...getEmptyFormat(val.columns[0]),
        };
      }
    }
    if (
      val.itemType === "bigHeader" ||
      val.itemType === "smallHeader" ||
      val.itemType === "image" ||
      val.itemType === "editor"
    ) {
      return;
    }
    answers[val.id] = val.itemType === "check" ? [] : null;
  });
  return answers;
}

function RequiredDocSubHeader(props: { text: string; notes: string | null }) {
  return (
    <div className="ScheduledVisitDocScreen__subHeader">
      <h3>{props.text}</h3>
      <hr />
      {props.notes === null ? null : (
        <div className={"ScheduledVisitDocScreen__boxCard"}>RN Notes: {props.notes}</div>
      )}
    </div>
  );
}

export function RequiredDocMainHeader(props: { text: string; withoutSeperator?: boolean }) {
  return (
    <div className="ScheduledVisitDocScreen__mainHeader">
      <h2>{props.text}</h2>
      {props.withoutSeperator === true ? null : <hr />}
    </div>
  );
}

export interface ReqDocAnswerableQuestionMulti extends RequiredDocAnswerableQuestionProps {
  possibleAnswers: string[];
  showOtherOption: boolean;
}

function showDocumentSaved() {
  const FADE_OUT_DELAY = 2000;

  const refreshTimeout = () => {
    fadeoutTimeout = setTimeout(() => {
      document
        .querySelectorAll(".ScheduledVisitDocScreen__documentChangesSaved")
        .forEach((currElem) => {
          currElem.classList.remove("fade-in");
          currElem.classList.add("fade-out");
        });
    }, FADE_OUT_DELAY - 550);

    saveTimeout = setTimeout(() => {
      document
        .querySelectorAll(".ScheduledVisitDocScreen__documentChangesSaved")
        .forEach((currElem) => {
          if (currElem.parentElement !== null) {
            currElem.parentElement.removeChild(currElem);
          }
        });
      saveTimeout = null;
    }, FADE_OUT_DELAY);
  };

  if (saveTimeout !== null && fadeoutTimeout !== null) {
    clearTimeout(fadeoutTimeout);
    clearTimeout(saveTimeout);
    refreshTimeout();
  } else {
    const elem = document.createElement("div");
    elem.innerText = translate(STRINGS.CHANGES_WERE_SAVED_IN_THE_CLOUD);
    elem.classList.add("ScheduledVisitDocScreen__documentChangesSaved");
    elem.classList.add("fade-in");
    document.body.appendChild(elem);
    refreshTimeout();
  }
}

function replaceArrayTemplates(
  arr: any[],
  documnetReplaceConstants: DocumentReplaceConstants
): void {
  for (let i = 0; i < arr.length; ++i) {
    if (typeof arr[i] === "string") {
      arr[i] = replaceStringTemplates(arr[i], documnetReplaceConstants);
    } else {
      replaceInnerObjTemplates(arr[i], documnetReplaceConstants);
    }
  }
}

function replaceObjectTemplates(
  obj: any,
  documnetReplaceConstants: DocumentReplaceConstants
): void {
  for (const key of Object.keys(obj)) {
    if (typeof obj[key] === "string") {
      obj[key] = replaceStringTemplates(obj[key], documnetReplaceConstants);
    } else {
      replaceInnerObjTemplates(obj[key], documnetReplaceConstants);
    }
  }
}

function replaceInnerObjTemplates(
  obj: any,
  documnetReplaceConstants: DocumentReplaceConstants
): void {
  if (typeof obj === "object" && obj !== null) {
    if (Array.isArray(obj)) {
      replaceArrayTemplates(obj, documnetReplaceConstants);
    } else {
      replaceObjectTemplates(obj, documnetReplaceConstants);
    }
  }
}

function replaceStringTemplates(
  str: string,
  documnetReplaceConstants: DocumentReplaceConstants
): string {
  let result = str;
  result = result.replace(/{agency_name}/g, documnetReplaceConstants.agencyName);
  result = result.replace(/{caregiver_name}/g, documnetReplaceConstants.caregiverName);
  result = result.replace(/{patient_name}/g, documnetReplaceConstants.patientName);
  result = result.replace(/{agency_address}/g, documnetReplaceConstants.agencyAddress);
  result = result.replace(/{agency_phone}/g, documnetReplaceConstants.agencyPhone);
  return result;
}
